import React, { useRef, useState, useCallback, useEffect } from "react"
import { RotateCcw, Upload, Video } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"

interface VideoRecorderProps {
  onUpload: (blob: Blob) => void
  isUploading: boolean
}

export default function VideoRecorder({
  onUpload,
  isUploading,
}: VideoRecorderProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [recording, setRecording] = useState(false)
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null)
  const [videoURL, setVideoURL] = useState<string | null>(null)
  const [timeLeft, setTimeLeft] = useState(0)
  const [cameraError, setCameraError] = useState<string | null>(null)

  const streamRef = useRef<MediaStream | null>(null)

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })
      setStream(mediaStream)
      streamRef.current = mediaStream
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }
    } catch (_err) {
      setCameraError(
        "Could not access your camera. Please check browser permissions and try again.",
      )
    }
  }, [])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      setStream(null)
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  useEffect(() => {
    startCamera()
    return () => {
      stopCamera()
    }
  }, [startCamera, stopCamera])

  // Timer logic
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (recording) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev + 1)
      }, 1000)
    } else {
      setTimeLeft(0)
    }
    return () => clearInterval(interval)
  }, [recording])

  const handleStartRecording = useCallback(() => {
    if (!stream) return

    setRecordedVideo(null)
    setVideoURL(null)

    const mediaRecorder = new MediaRecorder(stream)
    mediaRecorderRef.current = mediaRecorder
    const chunks: Blob[] = []

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data)
      }
    }

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" })
      setRecordedVideo(blob)
      setVideoURL(URL.createObjectURL(blob))
    }

    mediaRecorder.start()
    setRecording(true)
  }, [stream])

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop()
      setRecording(false)
      stopCamera()
    }
  }, [recording, stopCamera])

  const handleRetake = useCallback(() => {
    setRecordedVideo(null)
    setVideoURL(null)
    if (!stream) startCamera()
  }, [startCamera, stream])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  return (
    <>
      <div className="flex flex-col h-full bg-card rounded-2xl overflow-hidden shadow-sm relative">
        <div className="flex-1 relative bg-muted flex items-center justify-center rounded-t-2xl overflow-hidden">
          {!stream && !recordedVideo && (
            <div className="text-muted-foreground flex flex-col items-center justify-center text-center p-6">
              <div className="w-16 h-16 bg-card rounded-full flex items-center justify-center mb-4 shadow-sm">
                <Video className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                Camera Access Required
              </h3>
              <p className="max-w-xs mx-auto mb-6">
                Please enable camera access to record your video response.
              </p>
              <Button
                onClick={startCamera}
                className="bg-[#1b237a] text-white rounded-xl text-sm font-semibold hover:bg-[#1a2a8f] shadow-lg shadow-blue-900/20"
              >
                Enable Camera
              </Button>
            </div>
          )}

          {/* Live Preview */}
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-cover ${stream && !recordedVideo ? "block" : "hidden"}`}
          />

          {/* Recorded Playback */}
          {videoURL && (
            <video
              src={videoURL}
              controls
              className="w-full h-full object-contain"
            />
          )}

          {/* Recording Indicator & Timer */}
          {recording && (
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-red-600/90 text-white px-4 py-2 rounded-full backdrop-blur-sm shadow-sm transition-all">
              <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
              <span className="font-mono font-bold tracking-wider">
                {formatTime(timeLeft)}
              </span>
            </div>
          )}
        </div>

        {/* Controls Bar */}
        <div className="h-24 bg-card flex items-center justify-between px-8">
          {!recordedVideo ? (
            <div className="w-full flex items-center justify-center gap-8">
              {!recording ? (
                <button
                  onClick={handleStartRecording}
                  disabled={!stream}
                  className="group flex flex-col items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-16 h-16 rounded-full border-[3px] border-border flex items-center justify-center transition-all hover:border-red-500/30">
                    <div className="w-14 h-14 rounded-full bg-red-600 shadow-sm transition-all group-hover:scale-90" />
                  </div>
                  <span className="text-muted-foreground text-sm font-medium group-hover:text-red-600">
                    Record
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleStopRecording}
                  className="group flex flex-col items-center gap-2"
                >
                  <div className="w-16 h-16 rounded-full border-[3px] border-border flex items-center justify-center transition-all hover:scale-105 hover:border-red-500/30">
                    <div className="w-8 h-8 rounded-md bg-red-600 shadow-sm transition-transform group-hover:scale-90" />
                  </div>
                  <span className="text-muted-foreground text-sm font-medium group-hover:text-red-600">
                    Stop
                  </span>
                </button>
              )}
            </div>
          ) : (
            <div className="w-full flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleRetake}
                disabled={isUploading}
                className="gap-2"
              >
                <RotateCcw className="w-5 h-5" />
                Retake
              </Button>

              <Button
                onClick={() => {
                  if (recordedVideo) {
                    onUpload(recordedVideo)
                  } else {
                    toast.error("No recorded video to upload")
                  }
                }}
                disabled={isUploading}
                className="gap-2 bg-[#1b237a] hover:bg-[#1a2a8f] text-white rounded-xl font-semibold shadow-xl shadow-blue-900/20 transition-all hover:-translate-y-0.5"
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Submit Video
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Camera Error Dialog */}
      <Dialog
        open={!!cameraError}
        onOpenChange={(open) => !open && setCameraError(null)}
      >
        <DialogContent className="sm:max-w-[425px] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="size-5 text-destructive" />
              Error accessing camera
            </DialogTitle>
            <DialogDescription className="text-muted-foreground mt-2">
              {cameraError}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setCameraError(null)}>
              Dismiss
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setCameraError(null)
                startCamera()
              }}
            >
              Retry camera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
